import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './TermsAndConditionsModal.css';

interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TermsAndConditionsModal: React.FC<TermsAndConditionsModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }


  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCloseClick = () => {
    onClose();
  };

  const modalContent = (
    <div
      className="terms-modal-backdrop"
      onClick={handleBackdropClick}
    >
      <div className="terms-modal-container">
        <div className="terms-modal-header">
          <h2>Gymea Edu - Terms and Conditions</h2>
          <button
            className="terms-modal-close"
            onClick={handleCloseClick}
            type="button"
          >
            ×
          </button>
        </div>

        <div className="terms-modal-content">
          <div className="terms-content">
            <p><strong>Last Updated: 24 January 2026</strong></p>

            <h3>1. Acceptance of Terms</h3>
            <p>
              By accessing or using Gymea Edu (the "Website" or "Platform"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you must not use the Website. These Terms form a legally binding agreement between you ("the User") and Gymea Edu ("we", "us", or "our").
            </p>

            <h3>2. Purpose of the Platform</h3>
            <p>Gymea Edu is a professional tool designed to assist educators in:</p>
            <ul>
              <li>creating personalised teaching and learning plans</li>
              <li>drafting educational programs aligned with curriculum outcomes</li>
              <li>generating student report comments</li>
              <li>preparing correspondence and instructional materials</li>
            </ul>
            <p>
              All content generated by Gymea Edu is provided as draft material only. It is intended to support — not replace — professional judgment, curriculum expertise, and school compliance processes.
            </p>

            <h3>3. User Responsibility and Data Input</h3>
            <h4>3.1 De-Identification Requirement</h4>
            <p>
              You must not input any personally identifiable information, including but not limited to:
            </p>
            <ul>
              <li>full names</li>
              <li>dates of birth</li>
              <li>addresses</li>
              <li>contact details</li>
              <li>student or staff identification numbers</li>
            </ul>
            <p>You may only input de-identified behavioural, learning, or contextual information.</p>

            <h4>3.2 Prohibited Content</h4>
            <p>You must not input:</p>
            <ul>
              <li>health or medical records</li>
              <li>disciplinary or confidential personnel matters</li>
              <li>Cabinet-in-Confidence or restricted government material</li>
            </ul>

            <h4>3.3 User Responsibility</h4>
            <p>You are solely responsible for ensuring that:</p>
            <ul>
              <li>all data entered is properly de-identified</li>
              <li>your use complies with your employer's policies</li>
              <li>all outputs are reviewed before use</li>
              <li>no output is relied upon without professional verification</li>
            </ul>
            <p>Gymea Edu does not replace human judgment or compliance review.</p>

            <h3>4. AI Limitations</h3>
            <p>
              Gymea Edu uses generative artificial intelligence.<br />
              You acknowledge that:
            </p>
            <ul>
              <li>outputs may be inaccurate, incomplete, or outdated</li>
              <li>AI systems may produce incorrect or misleading content ("hallucinations")</li>
              <li>all outputs require verification</li>
              <li>Gymea Edu does not make decisions affecting student assessment or welfare</li>
            </ul>
            <p>Gymea Edu is a professional drafting assistant only.</p>

            <h3>5. Curriculum and Copyright Compliance</h3>
            <h4>5.1 Victorian Curriculum (VCAA)</h4>
            <p><strong>Required Notice:</strong></p>
            <p>
              The Victorian Curriculum F-10 content elements are © VCAA, reproduced by permission. Some curriculum elements in this resource may be modified extracts. The VCAA does not endorse or make any warranties regarding this resource and accepts no responsibility for incomplete or inaccurate information presented in this resource. The latest Victorian Curriculum F-10 and related content can be accessed directly at the VCAA website.
            </p>
            <p>Use of Victorian Curriculum content is permitted solely for educational and non-commercial purposes.</p>
            <p>
              Gymea Edu uses Victorian Curriculum F-10 materials made available by the Victorian Curriculum and Assessment Authority (VCAA).
            </p>
            <p>
              We take reasonable steps to ensure that Victorian Curriculum content made available through the Platform reflects the most current version published by VCAA.
            </p>
            <p>
              However, curriculum materials may be updated from time to time by VCAA, and there may be a delay before such updates are reflected within Gymea Edu.
            </p>
            <p>
              For authoritative and current versions of Victorian Curriculum F-10 materials, users must refer to the official VCAA website.
            </p>

            <h4>5.2 Australian Curriculum (ACARA)</h4>
            <p>
              The Australian Curriculum and National Literacy and Numeracy Learning Progressions are © ACARA and licensed under the Creative Commons Attribution 4.0 International (CC BY 4.0) licence.
            </p>
            <p>Use of these materials does not imply endorsement by ACARA. For official and current materials, users must refer to the Australian Curriculum website.</p>

            <h4>5.3 NSW Curriculum (NESA)</h4>
            <p>
              NSW Curriculum and Syllabus materials are © NSW Education Standards Authority (NESA) for and on behalf of the Crown in right of the State of New South Wales.
            </p>
            <p>Gymea Edu does not warrant currency or completeness of NSW syllabus content. Official materials must be accessed through NESA.</p>

            <h4>5.4 User Copyright Responsibility</h4>
            <p>You are responsible for ensuring that:</p>
            <ul>
              <li>your use of curriculum content complies with applicable licences</li>
              <li>you do not infringe copyright</li>
              <li>all generated content is lawfully used</li>
              <li>third-party materials are not reproduced unlawfully</li>
            </ul>

            <h3>6. Intellectual Property</h3>
            <p>All software, interface elements, prompts, templates, and system design remain the intellectual property of Gymea Edu.</p>
            <p>No ownership of VCAA, ACARA, or NESA materials is transferred.</p>
            <p>You are granted a limited, revocable, non-commercial licence to use generated content for professional educational purposes only.</p>
            <p>You must not:</p>
            <ul>
              <li>resell outputs</li>
              <li>distribute publicly</li>
              <li>sublicense</li>
              <li>claim ownership of curriculum materials</li>
            </ul>

            <h3>7. Privacy and Data Security</h3>
            <p>We take reasonable steps to protect user data. However:</p>
            <ul>
              <li>no system is fully secure</li>
              <li>we do not guarantee protection against unauthorised access</li>
              <li>we are not liable for interception or data loss</li>
            </ul>
            <p>We may analyse de-identified usage data to improve the Platform.</p>
            <p>We do not sell user data.</p>
            <p>You may request:</p>
            <ul>
              <li>access to your data</li>
              <li>correction</li>
              <li>deletion where legally permissible</li>
            </ul>
            <p>We operate under Australian privacy law and do not represent compliance with foreign regimes such as GDPR or FERPA.</p>

            <h3>8. Ethical and Professional Use</h3>
            <p>Users agree to use Gymea Edu consistently with:</p>
            <ul>
              <li>Australian AI Ethics Principles</li>
              <li>education department guidance</li>
              <li>professional teaching standards</li>
              <li>applicable privacy and copyright laws</li>
            </ul>

            <h3>9. Limitation of Liability</h3>
            <p>
              To the fullest extent permitted by law, Gymea Edu is not liable for any loss, damage, or consequence arising from:
            </p>
            <ul>
              <li>reliance on generated content</li>
              <li>curriculum misalignment</li>
              <li>privacy breaches from user input</li>
              <li>factual errors</li>
              <li>unauthorised access</li>
              <li>professional misuse</li>
            </ul>
            <p>All use is at your own risk.</p>
            <p>Nothing excludes rights under Australian Consumer Law.</p>

            <h3>10. Indemnity</h3>
            <p>You agree to indemnify and hold harmless Gymea Edu from any claim, loss, or liability arising from:</p>
            <ul>
              <li>your use of the Platform</li>
              <li>your data input</li>
              <li>your breach of these Terms</li>
              <li>copyright infringement</li>
              <li>misuse of curriculum materials</li>
            </ul>

            <h3>11. Service Availability</h3>
            <p>We do not guarantee uninterrupted service.</p>
            <p>We may modify, suspend, or discontinue the Platform at any time without liability.</p>

            <h3>12. Pricing and Subscriptions</h3>
            <p>Pricing and features may change without notice.</p>
            <p>Subscriptions renew automatically unless cancelled before renewal.</p>
            <p>Refunds are provided only where required by law.</p>

            <h3>13. Changes to Terms</h3>
            <p>We may update these Terms at any time. Continued use constitutes acceptance of revised Terms.</p>

            <h3>14. International Use</h3>
            <p>Gymea Edu is designed for Australian educators.</p>
            <p>Users outside Australia use the Platform at their own risk and must comply with local laws.</p>

            <h3>15. Governing Law and Jurisdiction</h3>
            <p>These Terms are governed by the laws of New South Wales, Australia.</p>
            <p>All disputes must be brought in the courts of New South Wales.</p>
            <p>Mandatory consumer rights remain unaffected.</p>

            <h3>16. Accessibility</h3>
            <p>Gymea Edu aims to comply with WCAG 2.1 AA accessibility standards.</p>

            <h3>17. Contact Us</h3>
            <p>
              If you have questions or concerns about these Terms, please{' '}
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSeb137Z79HtvYCeb59ppvQq8q9q76_AjHmd4tbi-GlUPAIGnw/viewform?usp=sharing&ouid=117310775465521436495"
                target="_blank"
                rel="noopener noreferrer"
                className="terms-contact-link"
              >
                contact us here
              </a>
              .
            </p>

            <p><strong>Effective Date: 24 January 2026</strong></p>
          </div>
        </div>

        <div className="terms-modal-footer">
          <button className="terms-modal-close-button" onClick={handleCloseClick} type="button">
            Close
          </button>
        </div>
      </div>
    </div>
  );

  // Use portal to render the modal at the document body level
  return createPortal(modalContent, document.body);
};
